import { useEffect } from 'react'
import type { AbstractAgent } from '@copilotkit/react-core/v2'
import { publishAIChatSceneEvent } from '@ui/events'
import useAIChatFindingsStore from 'src/state/stores/aiChatFindings'
import useUIStore from 'src/state/stores/ui'
import {
  MARK_ELEMENTS_EVENT,
  SHOW_FINDINGS_EVENT,
  SHOW_IFC_MODEL_EVENT,
  SHOW_STOREY_EVENT,
  createLiveRunTracker,
  elementGuidsOfMarkElementsEvent,
  findingsOfShowFindingsEvent,
  glbFileGuidOfShowIfcModelEvent,
  storeyOfShowStoreyEvent,
} from '../../utils'

/**
 * Jump the scene to the storey the agent works on (the AG-UI custom event
 * `show_storey`, e.g. per aligned storey plan), or show the IFC model whose
 * type, storey and filter assignments the agent just set (`show_ifc_model`),
 * so the user sees the result right away. Published as a scene event that the
 * project's editor applies (`AIChatSceneBridge`). Elements the agent marks
 * (`mark_elements`) are kept in the UI store, the findings of a check it
 * shows (`show_findings`) in the findings store; the editor reads them
 * and the chat's chip removes them. Only live runs count: the history
 * replayed on reconnect would jump through every storey again and restore
 * old markings (`createLiveRunTracker`).
 *
 * A subscription to the agent's event stream: the outside world this effect
 * exists for.
 */
const useSceneFocusOnAgentEvents = (projectId: string, agent: AbstractAgent): void => {
  useEffect(() => {
    const run = createLiveRunTracker()
    const subscription = agent.subscribe({
      ...run.callbacks,
      onCustomEvent: ({ event }) => {
        if (!run.isLive()) return
        if (event.name === MARK_ELEMENTS_EVENT) {
          const guids = elementGuidsOfMarkElementsEvent(event.value)
          if (guids !== null) useUIStore.getState().setAiChatMarkedElementGuids(projectId, guids)
          return
        }
        if (event.name === SHOW_FINDINGS_EVENT) {
          const findings = findingsOfShowFindingsEvent(event.value)
          if (findings !== null) useAIChatFindingsStore.getState().setFindings(projectId, findings)
          return
        }
        if (event.name === SHOW_IFC_MODEL_EVENT) {
          const glbFileGuid = glbFileGuidOfShowIfcModelEvent(event.value)
          if (glbFileGuid !== null) publishAIChatSceneEvent({ projectGuid: projectId, glbFileGuid })
          return
        }
        if (event.name !== SHOW_STOREY_EVENT) return
        const storey = storeyOfShowStoreyEvent(event.value)
        if (storey !== null) publishAIChatSceneEvent({ projectGuid: projectId, storey })
      },
    })
    return () => subscription.unsubscribe()
  }, [agent, projectId])
}

export default useSceneFocusOnAgentEvents
